import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  BookOpen, 
  Trash2, 
  Clock, 
  ChefHat,
  Palette,
  Search,
  Star,
  StarOff,
  Utensils,
  Baby,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

// Local storage key
const SAVED_RECIPES_KEY = 'calm_keeper_saved_recipes';

export const getSavedRecipes = () => {
  const stored = localStorage.getItem(SAVED_RECIPES_KEY);
  return stored ? JSON.parse(stored) : [];
};

export const saveRecipe = (recipe) => {
  const recipes = getSavedRecipes();
  const newRecipe = {
    ...recipe,
    id: Date.now(),
    savedAt: new Date().toISOString(),
    favorite: false,
  };
  recipes.unshift(newRecipe);
  localStorage.setItem(SAVED_RECIPES_KEY, JSON.stringify(recipes));
  return newRecipe;
};

export const removeRecipe = (id) => {
  const recipes = getSavedRecipes();
  const filtered = recipes.filter(r => r.id !== id);
  localStorage.setItem(SAVED_RECIPES_KEY, JSON.stringify(filtered));
  return filtered;
};

export const toggleFavorite = (id) => {
  const recipes = getSavedRecipes();
  const updated = recipes.map(r => 
    r.id === id ? { ...r, favorite: !r.favorite } : r
  );
  localStorage.setItem(SAVED_RECIPES_KEY, JSON.stringify(updated));
  return updated;
};

const RecipeCard = ({ recipe, onRemove, onToggleFavorite, onClick }) => {
  const isMeal = recipe.type === 'meal';
  
  return (
    <Card 
      className="p-4 bg-paper-light shadow-lifted border border-stone-200 cursor-pointer hover:shadow-lifted-lg transition-all relative overflow-hidden group"
      onClick={onClick}
    >
      {/* Type indicator */}
      <div className={`
        absolute top-0 left-0 right-0 h-1
        ${isMeal ? 'bg-brown' : 'bg-sage'}
      `} />
      
      {/* Favorite button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(recipe.id);
        }}
        variant="ghost"
        size="icon"
        className={`absolute top-2 right-2 h-8 w-8 ${recipe.favorite ? 'text-normal-text' : 'text-ink-300 opacity-0 group-hover:opacity-100'}`}
      >
        {recipe.favorite ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
      </Button>
      
      <div className="flex items-start gap-3 pt-2">
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${isMeal ? 'bg-brown/10' : 'bg-sage/10'}
        `}>
          {isMeal ? (
            <Utensils className="w-5 h-5 text-brown" />
          ) : (
            <Palette className="w-5 h-5 text-sage" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-ink-900 truncate pr-8">{recipe.name}</h4>
          <p className="text-xs text-ink-500 font-accent italic line-clamp-2 mt-1">
            {recipe.description}
          </p>
          
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={`text-xs ${isMeal ? 'bg-brown/10 text-brown' : 'bg-sage/10 text-sage-dark'}`}>
              {isMeal ? 'Meal' : 'Craft'}
            </Badge>
            {recipe.prep_time && (
              <Badge className="text-xs bg-paper-aged text-ink-700">
                <Clock className="w-3 h-3 mr-1" />
                {recipe.prep_time}
              </Badge>
            )}
            {recipe.time_required && (
              <Badge className="text-xs bg-paper-aged text-ink-700">
                <Clock className="w-3 h-3 mr-1" />
                {recipe.time_required}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(recipe.id);
        }}
        variant="ghost"
        size="icon"
        className="absolute bottom-2 right-2 h-8 w-8 text-ink-300 opacity-0 group-hover:opacity-100 hover:text-destructive"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </Card>
  );
};

const RecipeDetailDialog = ({ recipe, open, onClose }) => {
  if (!recipe) return null;
  
  const isMeal = recipe.type === 'meal';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            {isMeal ? (
              <ChefHat className="w-5 h-5 text-brown" />
            ) : (
              <Baby className="w-5 h-5 text-sage" />
            )}
            {recipe.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-ink-700 font-accent italic">{recipe.description}</p>
          
          <div className="flex flex-wrap gap-2">
            <Badge className={isMeal ? 'bg-brown/10 text-brown' : 'bg-sage/10 text-sage-dark'}>
              {isMeal ? 'Meal Idea' : 'Craft Activity'}
            </Badge>
            {recipe.difficulty && (
              <Badge className="bg-paper-aged text-ink-700">{recipe.difficulty}</Badge>
            )}
            {(recipe.prep_time || recipe.time_required) && (
              <Badge className="bg-paper-aged text-ink-700">
                <Clock className="w-3 h-3 mr-1" />
                {recipe.prep_time || recipe.time_required}
              </Badge>
            )}
            {recipe.age_appropriate && (
              <Badge className="bg-sage/10 text-sage-dark">{recipe.age_appropriate}</Badge>
            )}
          </div>
          
          {/* Ingredients / Materials */}
          {(recipe.ingredients_needed || recipe.materials_needed) && (
            <div>
              <h4 className="font-serif text-ink-900 mb-2">
                {isMeal ? 'Ingredients' : 'Materials'}
              </h4>
              <div className="flex flex-wrap gap-1">
                {(recipe.ingredients_needed || recipe.materials_needed || []).map((item, idx) => (
                  <span key={idx} className="text-sm bg-paper-aged px-2 py-1 rounded border border-stone-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Instructions (for crafts) */}
          {recipe.instructions && recipe.instructions.length > 0 && (
            <div>
              <h4 className="font-serif text-ink-900 mb-2">Instructions</h4>
              <ol className="list-decimal list-inside space-y-2">
                {recipe.instructions.map((step, idx) => (
                  <li key={idx} className="text-sm text-ink-900">{step}</li>
                ))}
              </ol>
            </div>
          )}
          
          {/* Saved date */}
          <p className="text-xs text-ink-500 font-accent italic pt-2 border-t border-stone-200">
            Saved on {new Date(recipe.savedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SavedRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  
  useEffect(() => {
    setRecipes(getSavedRecipes());
  }, []);
  
  const handleRemove = (id) => {
    const updated = removeRecipe(id);
    setRecipes(updated);
    toast.success('Recipe removed');
  };
  
  const handleToggleFavorite = (id) => {
    const updated = toggleFavorite(id);
    setRecipes(updated);
  };
  
  // Filter recipes
  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          recipe.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || 
                        (filterType === 'meals' && recipe.type === 'meal') ||
                        (filterType === 'crafts' && recipe.type === 'craft') ||
                        (filterType === 'favorites' && recipe.favorite);
    return matchesSearch && matchesType;
  });
  
  const mealCount = recipes.filter(r => r.type === 'meal').length;
  const craftCount = recipes.filter(r => r.type === 'craft').length;
  const favoriteCount = recipes.filter(r => r.favorite).length;
  
  return (
    <div className="space-y-6" data-testid="saved-recipes-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Saved Ideas
          </h2>
          <p className="text-ink-500 font-accent italic">
            {mealCount} meals • {craftCount} crafts • {favoriteCount} favorites 📚
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
          <BookOpen className="w-6 h-6 text-brown" />
        </div>
      </div>
      
      {/* Search and filters */}
      <Card className="p-4 bg-paper-light shadow-lifted border border-stone-200">
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-500" />
            <Input
              data-testid="recipe-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search saved ideas..."
              className="pl-9 border-stone-300"
            />
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: 'All', count: recipes.length },
            { id: 'meals', label: 'Meals', count: mealCount, icon: Utensils },
            { id: 'crafts', label: 'Crafts', count: craftCount, icon: Palette },
            { id: 'favorites', label: 'Favorites', count: favoriteCount, icon: Star },
          ].map((filter) => {
            const Icon = filter.icon;
            return (
              <Button
                key={filter.id}
                onClick={() => setFilterType(filter.id)}
                variant={filterType === filter.id ? "default" : "outline"}
                size="sm"
                className={`text-xs ${filterType === filter.id ? 'bg-brown text-white' : 'border-stone-300'}`}
              >
                {Icon && <Icon className="w-3 h-3 mr-1" />}
                {filter.label} ({filter.count})
              </Button>
            );
          })}
        </div>
      </Card>
      
      {/* Recipe grid */}
      {filteredRecipes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredRecipes.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              onRemove={handleRemove}
              onToggleFavorite={handleToggleFavorite}
              onClick={() => setSelectedRecipe(recipe)}
            />
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center bg-paper-light shadow-lifted border border-stone-200">
          <BookOpen className="w-12 h-12 text-ink-300 mx-auto mb-4" />
          <p className="text-ink-500 font-accent italic mb-2">
            {searchQuery || filterType !== 'all' 
              ? 'No ideas match your search' 
              : 'No saved ideas yet'
            }
          </p>
          <p className="text-xs text-ink-500">
            Save meal and craft ideas from the Resources page!
          </p>
        </Card>
      )}
      
      {/* Recipe detail dialog */}
      <RecipeDetailDialog
        recipe={selectedRecipe}
        open={!!selectedRecipe}
        onClose={() => setSelectedRecipe(null)}
      />
      
      {/* Tip */}
      <Card className="p-4 bg-warmth-sand border border-stone-300">
        <p className="text-sm text-ink-700 font-handwritten text-lg text-center">
          💡 Save your favorite AI-generated ideas to access them anytime!
        </p>
      </Card>
    </div>
  );
};

export default SavedRecipes;
